import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, Plus, X, Maximize2, Camera as CameraIcon, VideoOff } from 'lucide-react';
import { Header } from '../components/Header';
import { VideoPlayer } from '../components/VideoPlayer';
import { Modal } from '../components/Modal';
import { useGridStore } from '../store/gridStore';
import { camerasApi } from '../api';
import { Camera, demoCameras } from '../types';
import styles from './GridView.module.css';

const templateConfig: Record<'2x2' | '3x3' | '4x4', { cols: number; cells: number }> = {
  '2x2': { cols: 2, cells: 4 },
  '3x3': { cols: 3, cells: 9 },
  '4x4': { cols: 4, cells: 16 },
};

export function GridViewPage() {
  const navigate = useNavigate();
  const { template, cells, setTemplate, setCell, removeCell, clearAll } = useGridStore();
  const [availableCameras, setAvailableCameras] = useState<Camera[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCellIndex, setEditingCellIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCameras();
  }, []);

  const fetchCameras = async () => {
    setLoading(true);
    setError(null);
    try {
      // API уже возвращает плоские данные
      const data = await camerasApi.getAll();
      setAvailableCameras(data);
    } catch (err) {
      console.error('Failed to fetch cameras, using demo data:', err);
      setAvailableCameras(demoCameras);
      setError('Демо-режим: используются тестовые данные');
    } finally {
      setLoading(false);
    }
  };

  const cellsCount = templateConfig[template].cells;

  const paddedCells = useMemo(() => {
    const padded = [...cells];
    while (padded.length < cellsCount) {
      padded.push({ cameraId: null, streamUrl: null });
    }
    return padded.slice(0, cellsCount);
  }, [cells, cellsCount]);

  const activeCellsCount = useMemo(() => {
    return cells.filter((c) => c.cameraId !== null).length;
  }, [cells]);

  const handleOpenModal = (index: number) => {
    setEditingCellIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCellIndex(null);
  };

  const handleSelectCamera = (camera: Camera) => {
    if (editingCellIndex !== null) {
      setCell(editingCellIndex, camera.documentId, camera.streamUrl);
      handleCloseModal();
    }
  };

  const handleRemoveCamera = (index: number) => {
    removeCell(index);
  };

  const handleCellFullscreen = (index: number) => {
    const cell = document.querySelector(`[data-cell-index="${index}"]`);
    if (cell) {
      if (cell.requestFullscreen) {
        cell.requestFullscreen();
      }
    }
  };

  const handleListClick = () => {
    navigate('/cameras');
  };

  const handleLogout = () => {
    navigate('/login');
  };

  const getCameraForCell = (documentId: string | null): Camera | null => {
    if (!documentId) return null;
    return availableCameras.find((c) => c.documentId === documentId) || null;
  };

  const isCameraInGrid = (documentId: string): boolean => {
    return cells.some((c) => c.cameraId === documentId);
  };

  return (
    <div className={styles.page}>
      <Header currentPage="grid" onNavigate={handleListClick} onLogout={handleLogout} />

      <main className={styles.main}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.templateSelector}>
              {(['2x2', '3x3', '4x4'] as const).map((t) => (
                <button
                  key={t}
                  className={`${styles.templateButton} ${
                    template === t ? styles.templateButtonActive : ''
                  }`}
                  onClick={() => setTemplate(t)}
                  aria-pressed={template === t}
                >
                  {t}
                </button>
              ))}
            </div>

            <button className={styles.listButton} onClick={handleListClick}>
              <List size={18} />
              Список
            </button>

            <button 
              className={styles.clearButton} 
              onClick={clearAll}
              title="Очистить все ячейки"
            >
              Очистить всё
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            {error}
          </div>
        )}

        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <span>Загрузка камер...</span>
          </div>
        ) : (
          <div className={`${styles.grid} ${styles[`grid${template}`]}`}>
            {paddedCells.map((cell, index) => {
              const camera = getCameraForCell(cell.cameraId);
              return (
                <div key={index} className={styles.cell} data-cell-index={index}>
                  {camera ? (
                    <>
                      <div className={styles.cellPlayer}>
                        <VideoPlayer
                          streamUrl={cell.streamUrl || undefined}
                          autoPlay
                          showStatus
                          status={camera.cameraStatus}
                        />
                      </div>
                      <div className={styles.cellInfo}>
                        <span className={styles.cellName}>{camera.name}</span>
                      </div>
                      <div className={styles.cellActions}>
                        <button
                          className={`${styles.cellAction} ${styles.cellActionReplace}`}
                          onClick={() => handleOpenModal(index)}
                          aria-label="Заменить камеру"
                        >
                          <Plus size={14} />
                          Заменить
                        </button>
                        <button
                          className={`${styles.cellAction} ${styles.cellActionDelete}`}
                          onClick={() => handleRemoveCamera(index)}
                          aria-label="Удалить камеру"
                        >
                          <X size={14} />
                        </button>
                        <button
                          className={`${styles.cellAction} ${styles.cellActionFullscreen}`}
                          onClick={() => handleCellFullscreen(index)}
                          aria-label="Полноэкранный режим"
                        >
                          <Maximize2 size={14} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className={styles.emptyCell}>
                      <VideoOff size={32} className={styles.emptyIcon} />
                      <span className={styles.emptyText}>Камера не выбрана</span>
                      <button
                        className={styles.emptyButton}
                        onClick={() => handleOpenModal(index)}
                      >
                        <Plus size={16} />
                        Добавить камеру
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <footer className={styles.footer}>
          <div className={styles.footerStats}>
            <div className={styles.footerStat}>
              <span className={styles.footerLabel}>Активных потоков:</span>
              <span className={`${styles.footerValue} ${styles.footerValueActive}`}>
                {activeCellsCount}
              </span>
            </div>
            <div className={styles.footerStat}>
              <span className={styles.footerLabel}>Всего в сетке:</span>
              <span className={`${styles.footerValue} ${styles.footerValueTotal}`}>
                {activeCellsCount} / {cellsCount}
              </span>
            </div>
          </div>
        </footer>
      </main>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Выберите камеру"
      >
        <div className={styles.cameraList}>
          {availableCameras.map((camera) => {
            const isActive = isCameraInGrid(camera.documentId);
            return (
              <button
                key={camera.documentId}
                className={`${styles.cameraOption} ${
                  isActive ? styles.cameraOptionActive : ''
                }`}
                onClick={() => handleSelectCamera(camera)}
                disabled={isActive}
              >
                <div className={styles.cameraOptionIcon}>
                  <CameraIcon size={18} />
                </div>
                <div className={styles.cameraOptionContent}>
                  <div className={styles.cameraOptionName}>{camera.name}</div>
                  <div className={styles.cameraOptionMeta}>
                    {camera.address} • {camera.objectName}
                    {isActive && ' (уже добавлена)'}
                  </div>
                </div>
                <div
                  className={`${styles.cameraStatus} ${
                    camera.cameraStatus === 'active'
                      ? styles.cameraStatusActive
                      : styles.cameraStatusInactive
                  }`}
                />
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
